// composables/useSmoothAnchors.ts
import { onMounted, onBeforeUnmount } from 'vue'

export function useSmoothAnchors() {
  let clickHandler: ((e: MouseEvent) => void) | null = null

  onMounted(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return
    }

    clickHandler = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest<HTMLAnchorElement>('a[href]')
      if (!a) return

      const href = a.getAttribute('href')
      if (!href) return

      let hash: string | null = null

      // Case 1: Simple hash link (e.g., "#section")
      if (href.startsWith('#')) {
        hash = href
      }
      // Case 2: Full URL with hash (e.g., "https://domain.com/#section")
      else if (href.includes('#')) {
        try {
          const url = new URL(href, window.location.origin)
          // Only handle if it's the same origin (same domain)
          if (url.origin === window.location.origin && url.pathname === window.location.pathname) {
            hash = url.hash
          }
        } catch (err) {
          // Invalid URL, ignore
          console.info('error:', err)
          return
        }
      }

      if (!hash || hash === '#' || hash.length < 2) return

      const target = document.querySelector(hash)
      if (!target) return // nothing to scroll to

      e.preventDefault()

      // Update the URL hash without jumping
      history.pushState(null, '', hash)

      // Perform smooth scroll safely
      try {
        smoothScrollTo(target, 600);
      } catch (err) {
        console.error('Error scrolling to anchor:', err)
        // Fallback: instant scroll if smooth not supported
        target.scrollIntoView()
      }
    }

    document.addEventListener('click', clickHandler, { passive: false })
  })

  onBeforeUnmount(() => {
    if (clickHandler) {
      document.removeEventListener('click', clickHandler)
      clickHandler = null
    }
  })

  function smoothScrollTo(target: Element, duration: number = 600) {
    // Find the actual scrollable element first
    const scrollableElement = document.querySelector('.k-drawer-content')
      || document.querySelector('.main-content-wrapper')
      || document.querySelector('main')
      || document.documentElement;

    const startY = scrollableElement === document.documentElement ? window.scrollY : scrollableElement.scrollTop;

    // Calculate target position relative to the scrollable element
    const rect = target.getBoundingClientRect();
    const scrollableRect = scrollableElement === document.documentElement
      ? { top: 0 }
      : scrollableElement.getBoundingClientRect();

    const targetY = startY + rect.top - scrollableRect.top;
    const distance = targetY - startY;
    const startTime = performance.now();

    function step(currentTime: number) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // easeInOutQuad
      const ease = progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;

      const scrollY = startY + distance * ease;

      if (scrollableElement === document.documentElement) {
        window.scrollTo(0, scrollY);
      } else {
        scrollableElement.scrollTop = scrollY;
      }

      if (progress < 1) {
        requestAnimationFrame(step);
      }
    }

    requestAnimationFrame(step);
  }
}
